import { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { studentsApi, type StudentStatusDto } from '../../../lib/api/students.api';
import { errorText } from '../../../lib/error-text';
import { useTenantStore } from '../../../stores/tenant.store';
import { useTenantConfig } from '../../../hooks/use-tenant-config';
import {
  countsFor,
  DEFAULT_FILTER,
  filterStudents,
  programsOf,
  type StudentsFilter,
} from './students.model';

// ─── Alumnos (Gestión, núcleo) ──────────────────────────────────────────────
// Desde M4·D1 (2026-10-16) es del NÚCLEO, para todos los seminarios: dependía
// del flag `graduation_audit`, pero no toca el expediente ni el libro de notas.
// Todos los alumnos del seminario con su estado —egresado, activo, inactivo—
// calculado en el servidor con una sola regla del dominio (student-status.ts).
// Los fallecidos en el origen van ocultos por defecto; con el filtro aparecen
// con su marca. Cada fila lleva a la ficha de la persona; acá no hay acciones.

const STATUS_COLOR: Record<StudentStatusDto, 'success' | 'info' | 'default'> = {
  graduated: 'info',
  active: 'success',
  inactive: 'default',
};
const PAGE_SIZE = 50;

export function StudentsPage(): JSX.Element {
  const config = useTenantConfig();
  if (!config.settled) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }
  return <StudentsList />;
}

function StudentsList(): JSX.Element {
  const { t } = useTranslation('sis');
  const navigate = useNavigate();
  const { currentTenantId } = useTenantStore();
  const [filter, setFilter] = useState<StudentsFilter>(DEFAULT_FILTER);
  const [page, setPage] = useState(0);
  const query = useQuery({
    queryKey: ['students-list', currentTenantId],
    enabled: !!currentTenantId,
    queryFn: () => studentsApi.list({ tenantId: currentTenantId! }),
    staleTime: 60_000,
  });
  const rows = useMemo(
    () => (query.data ? filterStudents(query.data.rows, filter) : []),
    [query.data, filter],
  );
  const programs = useMemo(() => (query.data ? programsOf(query.data.rows) : []), [query.data]);
  const set = (patch: Partial<StudentsFilter>) => {
    setFilter((f) => ({ ...f, ...patch }));
    setPage(0);
  };

  if (query.isError) return <Alert severity="error">{errorText(query.error)}</Alert>;
  if (!query.data) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }
  const counts = countsFor(query.data, filter.showDeceased);
  const shown = rows.slice(page * PAGE_SIZE, page * PAGE_SIZE + PAGE_SIZE);

  return (
    <Stack spacing={3}>
      <Box>
        <Typography variant="h4" fontWeight={700}>
          {t('students.title')}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {query.data.currentTerm
            ? t('students.subtitle', { term: query.data.currentTerm.name })
            : t('students.subtitleNoTerm')}
        </Typography>
      </Box>

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        {(['graduated', 'active', 'inactive'] as const).map((s) => (
          <Card
            key={s}
            variant="outlined"
            sx={{
              flex: 1,
              borderRadius: 3,
              cursor: 'pointer',
              borderColor: filter.status === s ? 'primary.main' : undefined,
            }}
            onClick={() => set({ status: filter.status === s ? 'all' : s })}
          >
            <CardContent>
              <Typography variant="caption" color="text.secondary">
                {t(`students.status.${s}`)}
              </Typography>
              <Typography variant="h5" fontWeight={700}>
                {counts[s]}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {t(`students.statusHelp.${s}`)}
              </Typography>
            </CardContent>
          </Card>
        ))}
        <Card variant="outlined" sx={{ flex: 1, borderRadius: 3 }}>
          <CardContent>
            <Typography variant="caption" color="text.secondary">
              {t('students.total')}
            </Typography>
            <Typography variant="h5" fontWeight={700}>
              {counts.total}
            </Typography>
          </CardContent>
        </Card>
      </Stack>

      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }}>
        <TextField
          size="small"
          label={t('students.search')}
          value={filter.query}
          onChange={(e) => set({ query: e.target.value })}
          sx={{ minWidth: 260 }}
        />
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel id="students-status">{t('students.filters.status')}</InputLabel>
          <Select
            labelId="students-status"
            label={t('students.filters.status')}
            value={filter.status}
            onChange={(e) => set({ status: e.target.value as StudentsFilter['status'] })}
          >
            <MenuItem value="all">{t('students.filters.all')}</MenuItem>
            <MenuItem value="graduated">{t('students.status.graduated')}</MenuItem>
            <MenuItem value="active">{t('students.status.active')}</MenuItem>
            <MenuItem value="inactive">{t('students.status.inactive')}</MenuItem>
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 220 }}>
          <InputLabel id="students-program">{t('students.filters.program')}</InputLabel>
          <Select
            labelId="students-program"
            label={t('students.filters.program')}
            value={filter.program}
            onChange={(e) => set({ program: e.target.value })}
          >
            <MenuItem value="all">{t('students.filters.all')}</MenuItem>
            {programs.map((p) => (
              <MenuItem key={p} value={p}>
                {p}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControlLabel
          control={
            <Switch
              checked={filter.showDeceased}
              onChange={(e) => set({ showDeceased: e.target.checked })}
            />
          }
          label={t('students.filters.showDeceased')}
        />
      </Stack>

      <Card variant="outlined" sx={{ borderRadius: 3 }}>
        {rows.length === 0 ? (
          <CardContent>
            <Typography variant="body2" color="text.secondary">
              {t('students.empty')}
            </Typography>
          </CardContent>
        ) : (
          <>
            <TableContainer>
              <Table size="small" aria-label={t('students.title')}>
                <TableHead>
                  <TableRow
                    sx={{ '& th': { color: 'text.secondary', fontWeight: 600, fontSize: 12 } }}
                  >
                    <TableCell>{t('students.columns.student')}</TableCell>
                    <TableCell>{t('students.columns.program')}</TableCell>
                    <TableCell>{t('students.columns.status')}</TableCell>
                    <TableCell>{t('students.columns.lastTerm')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {shown.map((r) => (
                    <TableRow
                      key={r.personId}
                      hover
                      sx={{ cursor: 'pointer' }}
                      onClick={() => navigate(`/people/${r.personId}`)}
                    >
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>
                          {`${r.firstName} ${r.lastName}`.trim()}
                        </Typography>
                        {r.studentCode && (
                          <Typography variant="caption" color="text.secondary">
                            {r.studentCode}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>{r.programs.length ? r.programs.join(', ') : '—'}</TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                          <Chip
                            size="small"
                            color={STATUS_COLOR[r.status]}
                            variant={r.status === 'inactive' ? 'outlined' : 'filled'}
                            label={t(`students.statusOne.${r.status}`)}
                          />
                          {r.deceasedInSource && (
                            <Chip size="small" variant="outlined" label={t('students.deceased')} />
                          )}
                        </Stack>
                      </TableCell>
                      <TableCell>{r.lastTermName ?? '—'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={rows.length}
              page={page}
              rowsPerPage={PAGE_SIZE}
              rowsPerPageOptions={[PAGE_SIZE]}
              onPageChange={(_, p) => setPage(p)}
              labelDisplayedRows={({ from, to, count }) =>
                t('students.pagination', { from, to, count })
              }
            />
          </>
        )}
      </Card>
    </Stack>
  );
}
